/**
 * TenantAdminPage - World-Class Minimalist Design
 * Organization settings with clean, elegant UI
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Building2, Shield, Settings,
  Loader2, BarChart3, Activity, Network,
  Briefcase, Sparkles, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useTranslation } from '../context/LanguageContext';
import { teamsApi, settingsApi } from '../services';

// Import tabs
import {
  UsersTab,
  TeamsTab,
  DepartmentsTab,
  SettingsTab,
  StatsTab,
  WorkloadBoardTab,
  OrgChartTab
} from './TenantAdminPage/tabs';

// Import modals
import {
  InviteUserModal,
  EditUserModal,
  CreateTeamModal,
  CreateDepartmentModal
} from './TenantAdminPage/modals';

// Import Agent Dashboard
import AdminAgentDashboard from '../components/admin/AdminAgentDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Translations
const translations = {
  en: {
    organization: 'Organization',
    admin: 'Administration',
    noAccess: 'Access Denied',
    noAccessDesc: 'You need Tenant Admin permissions to access this page.',
    users: 'Users',
    orgChart: 'Org Chart',
    resources: 'Resources & Capacity',
    aiAgents: 'AI Agents',
    teams: 'Teams',
    departments: 'Departments',
    settings: 'Settings',
    statistics: 'Statistics',
    loading: 'Loading...',
    couldNotLoadData: 'Could not load data'
  },
  no: {
    organization: 'Organisasjon',
    admin: 'Administrasjon',
    noAccess: 'Ingen tilgang',
    noAccessDesc: 'Du må være Tenant Admin for å få tilgang til denne siden.',
    users: 'Brukere',
    orgChart: 'Organisasjonskart',
    resources: 'Ressurser & Kapasitet',
    aiAgents: 'AI Agenter',
    teams: 'Team',
    departments: 'Avdelinger',
    settings: 'Innstillinger',
    statistics: 'Statistikk',
    loading: 'Laster...',
    couldNotLoadData: 'Kunne ikke laste data'
  }
};

const TenantAdminPage = () => {
  const navigate = useNavigate();
  const { language } = useTranslation();
  const t = translations[language] || translations.no;
  
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [orgChart, setOrgChart] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  
  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showCreateDepartmentModal, setShowCreateDepartmentModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (activeTab === 'users') {
        const response = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data.users || []);
      } else if (activeTab === 'teams') {
        const response = await axios.get(`${API}/teams/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeams(response.data.teams || []);
      } else if (activeTab === 'departments') {
        const response = await axios.get(`${API}/admin/departments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDepartments(response.data.departments || []);
      } else if (activeTab === 'settings') {
        const response = await axios.get(`${API}/admin/tenant`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTenantInfo(response.data.tenant || {});
      } else if (activeTab === 'stats') {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data.statistics || response.data || {});
      } else if (activeTab === 'orgchart') {
        const response = await axios.get(`${API}/admin/org/chart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrgChart(response.data || null);
      } else if (activeTab === 'workload') {
        const response = await axios.get(`${API}/admin/org/workload`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWorkload(response.data || null);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t.couldNotLoadData);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  // Access control - allow ADMIN role (previously TENANT_ADMIN)
  if (currentUser.role !== 'TENANT_ADMIN' && currentUser.role !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FAFAF9]">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-stone-200 shadow-sm p-10 max-w-md text-center"
        >
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-stone-900 mb-3">{t.noAccess}</h2>
          <p className="text-stone-500 leading-relaxed">{t.noAccessDesc}</p>
        </motion.div>
      </div>
    );
  }

  // Tab configuration with translations
  const tabs = [
    { id: 'users', label: t.users, icon: Users },
    { id: 'orgchart', label: t.orgChart, icon: Network },
    { id: 'workload', label: t.resources, icon: Activity },
    { id: 'agents', label: t.aiAgents, icon: Sparkles },
    { id: 'teams', label: t.teams, icon: Building2 },
    { id: 'departments', label: t.departments, icon: Briefcase },
    { id: 'settings', label: t.settings, icon: Settings },
    { id: 'stats', label: t.statistics, icon: BarChart3 }
  ];

  return (
    <div className="h-screen flex bg-white overflow-hidden">
      {/* Main Sidebar */}
      <Sidebar 
        activeView="settings" 
        onNavigate={(view) => navigate(`/lexy/dashboard?tab=${view}`)}
        isExpanded={sidebarExpanded}
        onToggle={() => setSidebarExpanded(!sidebarExpanded)}
      />

      {/* Main Content Area - adjusts with sidebar */}
      <div className={`flex-1 flex overflow-hidden transition-all duration-300 ${sidebarExpanded ? 'ml-56' : 'ml-14'}`}>
        
        {/* Organization Sidebar Nav - Dark theme like case page */}
        <div className="h-full flex flex-col bg-[#141414] w-56 flex-shrink-0">
          {/* Header */}
          <div className="flex-shrink-0 px-4 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-medium text-white/85 truncate">{t.organization}</h1>
                <p className="text-xs text-white/40">{t.admin}</p>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="flex-1 py-3 px-2 overflow-y-auto">
            <div className="space-y-0.5">
              {tabs.slice(0, 4).map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-md
                      transition-all duration-150
                      ${isActive 
                        ? 'bg-white/[0.06] text-white/85 border-l-[2px] border-white/30 -ml-0.5 pl-2' 
                        : 'text-white/30 hover:text-white/55 hover:bg-white/[0.02]'
                      }
                    `}
                  >
                    <tab.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-white/80' : 'text-white/25'}`} strokeWidth={1.5} />
                    <span className={`flex-1 text-left text-[13px] tracking-wide ${isActive ? 'font-medium' : 'font-normal'}`}>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* MORE Section */}
            <div className="mt-4 pt-3 border-t border-white/[0.06]">
              <p className="px-2.5 mb-2 text-[10px] font-medium text-white/20 uppercase tracking-wider">More</p>
              <div className="space-y-0.5">
                {tabs.slice(4).map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-md
                        transition-all duration-150
                        ${isActive 
                          ? 'bg-white/[0.06] text-white/85 border-l-[2px] border-white/30 -ml-0.5 pl-2' 
                          : 'text-white/30 hover:text-white/55 hover:bg-white/[0.02]'
                        }
                      `}
                    >
                      <tab.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-white/80' : 'text-white/25'}`} strokeWidth={1.5} />
                      <span className={`flex-1 text-left text-[13px] tracking-wide ${isActive ? 'font-medium' : 'font-normal'}`}>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="p-8 max-w-5xl mx-auto">
            <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div 
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center py-20"
                  >
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                      <span className="text-sm text-slate-400">{t.loading}</span>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeTab === 'users' && (
                      <UsersTab 
                        users={users}
                        teams={teams}
                        onInvite={() => setShowInviteModal(true)}
                        onEdit={handleEditUser}
                        onRefresh={loadData}
                        language={language}
                      />
                    )}

                    {activeTab === 'teams' && (
                      <TeamsTab
                        teams={teams}
                        users={users}
                        onCreate={() => setShowCreateTeamModal(true)}
                        onRefresh={loadData}
                        language={language}
                      />
                    )}

                    {activeTab === 'departments' && (
                      <DepartmentsTab
                        departments={departments}
                        users={users}
                        onCreate={() => setShowCreateDepartmentModal(true)}
                        onRefresh={loadData}
                        language={language}
                      />
                    )}

                    {activeTab === 'workload' && (
                      <WorkloadBoardTab workload={workload} onRefresh={loadData} language={language} />
                    )}

                    {activeTab === 'orgchart' && (
                      <OrgChartTab orgChart={orgChart} onRefresh={loadData} language={language} />
                    )}

                    {activeTab === 'settings' && (
                      <SettingsTab
                        tenant={tenantInfo}
                        onRefresh={loadData}
                        language={language}
                      />
                    )}

                    {activeTab === 'stats' && (
                      <StatsTab stats={stats} language={language} />
                    )}

                    {activeTab === 'agents' && (
                      <AdminAgentDashboard language={language} />
                    )}
                  </motion.div>
                )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showInviteModal && (
          <InviteUserModal
            onClose={() => setShowInviteModal(false)}
            onSuccess={() => { loadData(); setShowInviteModal(false); }}
            teams={teams}
            departments={departments}
            language={language}
          />
        )}

        {showEditUserModal && selectedUser && (
          <EditUserModal
            user={selectedUser}
            onClose={() => { setShowEditUserModal(false); setSelectedUser(null); }}
            onSuccess={() => { loadData(); setShowEditUserModal(false); setSelectedUser(null); }}
            teams={teams}
            departments={departments}
          />
        )}

        {showCreateTeamModal && (
          <CreateTeamModal
            onClose={() => setShowCreateTeamModal(false)}
            onSuccess={() => { loadData(); setShowCreateTeamModal(false); }}
            users={users}
            departments={departments}
          />
        )}

        {showCreateDepartmentModal && (
          <CreateDepartmentModal
            onClose={() => setShowCreateDepartmentModal(false)}
            onSuccess={() => { loadData(); setShowCreateDepartmentModal(false); }}
            users={users}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TenantAdminPage;
